import React from 'react'
import Select from 'react-select'
import {
  CARD_FIELD_OPTIONS,
  Opt,
  SourceCode,
  sortOptionsForSources,
} from '../../../constants/carouselOptions'
import formStyles from '../../../styles/forms.module.scss'
import selectStyles from '../../../styles/react-select.module.scss'
import type { CarouselMetadata } from './CarouselEditor'
import styles from './CardCarouselEditor.module.scss'

// How the rail presents its content, as opposed to which content it holds.
// Rendered inside WidgetForm's Basics section next to name, title and size, so
// the whole "what is this rail" picture sits in one group.
//
// Everything except the limit depends on the source set, which is chosen
// further down in Content: sort options are derived per source, and ended
// grace and live_first are matches-only. Those fields therefore appear once a
// source is picked rather than sitting inert. The limit is always shown — it
// caps a fully curated rail too.

interface CarouselRailSettingsProps {
  metadata: CarouselMetadata
  onChange: (metadata: CarouselMetadata) => void
}

const CarouselRailSettings: React.FC<CarouselRailSettingsProps> = ({ metadata, onChange }) => {
  const sources = (metadata.sources as SourceCode[]) ?? []
  const sortOpts = sortOptionsForSources(sources)
  const hasSources = sources.length > 0
  const isMatches = sources.includes('matches')

  const apply = (patch: Partial<CarouselMetadata>) => {
    const next: CarouselMetadata = { ...metadata, ...patch }
    for (const k of Object.keys(next)) {
      if (next[k] === undefined) delete next[k]
    }
    onChange(next)
  }

  return (
    <>
      <div className={styles.settingsGrid}>
        {hasSources && (
          <div className={formStyles.fieldGroup}>
            <label>Sort</label>
            <Select<Opt, false>
              options={sortOpts}
              value={sortOpts.find(o => o.value === metadata.sort) ?? null}
              onChange={(o) => apply({ sort: o?.value })}
              className={selectStyles.reactSelect}
              classNamePrefix="react-select"
              placeholder="Default order"
            />
          </div>
        )}
        <div className={formStyles.fieldGroup}>
          <label title="How many cards the rail holds in total, pins included.">Limit</label>
          <input
            type="number"
            min={1}
            max={100}
            placeholder="20"
            value={metadata.limit ?? ''}
            onChange={(e) => apply({ limit: e.target.value ? parseInt(e.target.value, 10) : undefined })}
          />
        </div>
        {isMatches && (
          <div className={formStyles.fieldGroup}>
            <label title="How long a finished match lingers on a live rail before dropping off.">Ended grace (min)</label>
            <input
              type="number"
              min={0}
              placeholder="e.g. 45"
              title="How long a finished match lingers on a live rail before dropping off."
              value={metadata.ended_grace_minutes ?? ''}
              onChange={(e) => apply({ ended_grace_minutes: e.target.value ? parseInt(e.target.value, 10) : undefined })}
            />
          </div>
        )}
      </div>

      {isMatches && (
        <div className={formStyles.fieldGroup}>
          <label className={formStyles.checkboxLabel}>
            <input
              type="checkbox"
              checked={metadata.live_first === true}
              onChange={(e) => apply({ live_first: e.target.checked ? true : undefined })}
            />
            Live matches first
          </label>
        </div>
      )}

      {hasSources && (
        <div className={formStyles.fieldGroup}>
          <label title="Which metadata renders on each card.">Card fields</label>
          <Select<Opt, true>
            isMulti
            options={CARD_FIELD_OPTIONS}
            value={CARD_FIELD_OPTIONS.filter(o => (metadata.card_fields ?? []).includes(o.value))}
            onChange={(v) => apply({ card_fields: v.length ? v.map(o => o.value) : undefined })}
            className={selectStyles.reactSelect}
            classNamePrefix="react-select"
            placeholder="Default set"
          />
        </div>
      )}
    </>
  )
}

export default CarouselRailSettings
